import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import { useParams } from 'react-router'
import { Link } from 'react-router-dom'
import { format } from 'date-fns'
import { Box, Group, SimpleGrid, Divider } from '@mantine/core'
import LoadingComponent from '../../app/layout/LoadingComponents'
import Breadcrumb from '../../app/common/Breadcrumb'
import PartsCornerOverlay from '../../app/common/PartsCornerOverlay'
import { AppBadge, AppButton, AppText } from '../../app/common/ui'
import { useStore } from '../../app/stores/store'
import PartsDisplay from './PartsDisplay'

function formatCurrency (value: number | null | undefined) {
  if (value == null) return '—'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value)
}

export default observer(function InventoryDetail () {
  const { firearmStore, userStore } = useStore()
  const { selectedFirearm: firearm, loadFirearm, loadingInitial } = firearmStore
  const { id } = useParams<{ id: string }>()

  useEffect(() => {
    if (id) loadFirearm(id)
    return () => firearmStore.clearSelectedFirearm()
  }, [id, loadFirearm, firearmStore])

  if (loadingInitial || !firearm) {
    return <LoadingComponent content="Loading..." />
  }

  const mainImage = firearm.images?.find((i) => i.isMain)?.url || firearm.coverImageUrl
  const partsValue = (firearm.parts ?? []).reduce((sum, p) => sum + (p.value || 0), 0)
  const totalValue = (firearm.currentValue ?? 0) + partsValue

  const breadcrumbItems = [
    { label: 'Inventory', href: '/inventory' },
    { label: `${firearm.manufacturer} ${firearm.model}` }
  ]

  const isAuthor = userStore.user?.username === firearm.authorUsername

  const specItems = [
    { label: 'Type', value: firearm.type },
    { label: 'Serial number', value: firearm.serialNumber },
    { label: 'Caliber', value: firearm.caliber },
    { label: 'Barrel length', value: firearm.barrelLength },
    { label: 'Weight', value: firearm.weight },
    { label: 'Year', value: firearm.yearManufactured },
    { label: 'Action', value: firearm.actionType },
    { label: 'Finish', value: firearm.finishType },
    { label: 'Stock', value: firearm.stockType },
    { label: 'Magazine capacity', value: firearm.magazineCapacity != null ? String(firearm.magazineCapacity) : null },
    { label: 'Barrel twist', value: firearm.barrelTwistRate },
    { label: 'Sights', value: firearm.sightsType }
  ].filter((x) => x.value)

  const conditionItems = [
    { label: 'Overall', value: firearm.conditionGrade },
    { label: 'Bore', value: firearm.boreCondition },
    { label: 'Stock', value: firearm.stockCondition },
    { label: 'Mechanical', value: firearm.mechanicalCondition }
  ].filter((x) => x.value)

  const valueItems = [
    { label: 'Purchase price', value: firearm.purchasePrice },
    { label: 'Purchase date', value: firearm.purchaseDate ? format(new Date(firearm.purchaseDate), 'MMM d, yyyy') : null },
    { label: 'Current value', value: firearm.currentValue },
    { label: 'Appraisal value', value: firearm.appraisalValue },
    { label: 'Appraisal date', value: firearm.appraisalDate ? format(new Date(firearm.appraisalDate), 'MMM d, yyyy') : null }
  ].filter((x) => x.value != null && x.value !== '')

  return (
    <Box sx={{ maxWidth: 860, margin: '0 auto' }}>
      <Box mb="md" sx={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <AppText
          component={Link}
          to="/inventory"
          size="sm"
          sx={{ color: 'var(--gs-muted)', textDecoration: 'none', '&:hover': { color: 'var(--gs-text)' } }}
        >
          ← Back to Inventory
        </AppText>
        <Breadcrumb items={breadcrumbItems} />
      </Box>

      <Box
        sx={{
          background: 'var(--gs-surface)',
          border: '1px solid var(--gs-border)',
          borderRadius: 8,
          padding: 'var(--gs-lg)'
        }}
      >
        <Box mb="lg">
          <Group position="apart" align="flex-start">
            <Box>
              <AppText sx={{ fontFamily: 'Oswald, sans-serif', fontWeight: 700, fontSize: 24, color: 'var(--gs-text)' }}>
                {firearm.manufacturer} {firearm.model}
              </AppText>
              {(firearm.countryOfOrigin || firearm.conditionGrade) && (
                <Group spacing="xs" mt={4}>
                  {firearm.countryOfOrigin && (
                    <AppText size="sm" color="dimmed">
                      {firearm.countryOfOrigin}
                    </AppText>
                  )}
                  {firearm.conditionGrade && (
                    <AppBadge variant="light" color="blue" radius="xl">
                      {firearm.conditionGrade}
                    </AppBadge>
                  )}
                </Group>
              )}
            </Box>
            {isAuthor && (
              <AppButton component={Link} to={`/manageFirearm/${firearm.id}`} variant="light" size="sm">
                Edit
              </AppButton>
            )}
          </Group>
        </Box>

        {mainImage && (
          <Box sx={{ position: 'relative', marginBottom: 'var(--gs-md)' }}>
            <Box sx={{ borderRadius: 8, overflow: 'hidden', maxHeight: 360 }}>
              <img src={mainImage} alt="" style={{ width: '100%', objectFit: 'cover' }} />
            </Box>
            <PartsCornerOverlay parts={firearm.parts ?? []} />
          </Box>
        )}

        <Box mb="xl" sx={{ borderBottom: '1px solid var(--gs-border)', paddingBottom: 20 }}>
          <AppText size="xs" color="dimmed" transform="uppercase" weight={600}>
            Total estimated value
          </AppText>
          <AppText size="2rem" weight={800} sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatCurrency(totalValue)}
          </AppText>
          {(firearm.currentValue != null || partsValue > 0) && (
            <AppText size="sm" color="dimmed" mt={4}>
              {firearm.currentValue != null && formatCurrency(firearm.currentValue) + ' firearm'}
              {firearm.currentValue != null && partsValue > 0 && ' + '}
              {partsValue > 0 && formatCurrency(partsValue) + ' in parts'}
            </AppText>
          )}
        </Box>

        {(specItems.length > 0 || conditionItems.length > 0 || valueItems.length > 0) && (
          <>
            <Divider my="xl" label="Specifications & condition" labelPosition="center" />
            <SimpleGrid cols={1} breakpoints={[{ minWidth: 480, cols: 2 }, { minWidth: 768, cols: 3 }]} spacing="lg">
              {specItems.length > 0 && (
                <Box>
                  <AppText size="xs" color="dimmed" transform="uppercase" weight={600} mb="xs">
                    Specifications
                  </AppText>
                  {specItems.map(({ label, value }) => (
                    <Group key={label} position="apart" py={4}>
                      <AppText size="sm" color="dimmed">{label}</AppText>
                      <AppText size="sm" weight={500}>{String(value)}</AppText>
                    </Group>
                  ))}
                </Box>
              )}
              {conditionItems.length > 0 && (
                <Box>
                  <AppText size="xs" color="dimmed" transform="uppercase" weight={600} mb="xs">
                    Condition
                  </AppText>
                  {conditionItems.map(({ label, value }) => (
                    <Group key={label} position="apart" py={4}>
                      <AppText size="sm" color="dimmed">{label}</AppText>
                      <AppText size="sm" weight={500}>{String(value)}</AppText>
                    </Group>
                  ))}
                </Box>
              )}
              {valueItems.length > 0 && (
                <Box>
                  <AppText size="xs" color="dimmed" transform="uppercase" weight={600} mb="xs">
                    Value history
                  </AppText>
                  {valueItems.map(({ label, value }) => (
                    <Group key={label} position="apart" py={4}>
                      <AppText size="sm" color="dimmed">{label}</AppText>
                      <AppText size="sm" weight={500} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {typeof value === 'number' ? formatCurrency(value) : value}
                      </AppText>
                    </Group>
                  ))}
                </Box>
              )}
            </SimpleGrid>
          </>
        )}

        {(firearm.locationStored || firearm.acquisitionType || firearm.inventoryNotes) && (
          <>
            <Divider my="xl" label="Storage & notes" labelPosition="center" />
            <Box>
              {firearm.locationStored && (
                <Group spacing="xs" mb="xs">
                  <AppText size="sm" color="dimmed">Stored:</AppText>
                  <AppText size="sm">{firearm.locationStored}</AppText>
                </Group>
              )}
              {firearm.acquisitionType && (
                <Group spacing="xs" mb="xs">
                  <AppText size="sm" color="dimmed">Acquisition:</AppText>
                  <AppText size="sm">{firearm.acquisitionType}</AppText>
                </Group>
              )}
              {firearm.inventoryNotes && (
                <Box mt="sm">
                  <AppText size="sm" color="dimmed" mb={4}>Notes</AppText>
                  <AppText size="sm">{firearm.inventoryNotes}</AppText>
                </Box>
              )}
            </Box>
          </>
        )}

        <Divider my="xl" label="Custom parts" labelPosition="center" />
        <PartsDisplay
          parts={firearm.parts ?? []}
          totalValue={partsValue}
          editable={false}
        />
      </Box>
    </Box>
  )
})
